import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { toast } from "react-toastify";
import DistributionReviewApi from "../../../../../infrastructure/api/distributionReview";

const pickValue = (obj, camel, pascal) => (obj ? obj[camel] ?? obj[pascal] : undefined);

const toAreaOptions = (items) =>
  (items || []).map((a) => ({
    id: Number(pickValue(a, "id", "Id")),
    title: pickValue(a, "title", "Title") || "—",
  }));

/**
 * دکمه ارجاع به کارشناس توزیع پس از تکمیل نظارت ناظر سولارنت.
 * اگر شهر درخواست زیر پوشش چند امور باشد یا پوشش ثبت نشده باشد، انتخاب امور الزامی است.
 */
const ReferToDistributionPanel = ({ requestId }) => {
  const [routing, setRouting] = useState(null);
  const [areaId, setAreaId] = useState("");
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    if (!requestId) return;
    try {
      const res = await DistributionReviewApi.getRoutingStatusAsSupervisor(requestId);
      setRouting(res?.data ?? res?.Data ?? null);
    } catch {
      setRouting(null);
    }
  }, [requestId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const review = pickValue(routing, "review", "Review");
  const stage = pickValue(review, "stage", "Stage");
  const needsRefer =
    stage === 3 || stage === "AwaitingDistributionReferral" || String(stage) === "3";

  const resolvedAreaId = pickValue(routing, "distributionAreaId", "DistributionAreaId");
  const areaSelectionRequired =
    Boolean(pickValue(routing, "areaSelectionRequired", "AreaSelectionRequired")) || !resolvedAreaId;

  const { areaOptions, fromCoverage } = useMemo(() => {
    const candidates = toAreaOptions(pickValue(routing, "candidateAreas", "CandidateAreas"));
    if (candidates.length > 0) return { areaOptions: candidates, fromCoverage: true };
    return {
      areaOptions: toAreaOptions(pickValue(routing, "companyAreas", "CompanyAreas")),
      fromCoverage: false,
    };
  }, [routing]);

  if (!requestId || !needsRefer) return null;

  const companyName = pickValue(routing, "distributionCompanyName", "DistributionCompanyName");
  const areaName = pickValue(routing, "distributionAreaName", "DistributionAreaName");

  const refer = async () => {
    if (areaSelectionRequired && !areaId) {
      toast.error("امور برق را برای ارجاع انتخاب کنید");
      return;
    }
    setSaving(true);
    try {
      await DistributionReviewApi.referToDistributionAsSupervisor({
        requestId: Number(requestId),
        distributionAreaId: areaSelectionRequired ? Number(areaId) : Number(resolvedAreaId),
      });
      toast.success("به کارشناس شرکت توزیع ارجاع شد");
      setAreaId("");
      await reload();
    } catch (e) {
      toast.error(e?.message || "خطا در ارجاع به توزیع");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <Stack spacing={1}>
        <Typography variant="body2">
          نظارت سولارنت تکمیل شده است. لطفاً پرونده را به کارشناس شرکت توزیع
          {companyName ? ` «${companyName}»` : ""}
          {!areaSelectionRequired && areaName ? ` (امور «${areaName}»)` : ""} ارجاع دهید.
        </Typography>
        {areaSelectionRequired ? (
          <>
            <Typography variant="body2">
              {fromCoverage
                ? "شهر این درخواست زیر پوشش چند امور است؛ امور مربوط را انتخاب کنید."
                : "پوشش شهر این درخواست در امورهای شرکت ثبت نشده است؛ امور مربوط را انتخاب کنید."}
            </Typography>
            <TextField
              select
              size="small"
              label="امور برق"
              value={areaId}
              onChange={(e) => setAreaId(e.target.value)}
              sx={{ minWidth: 260, bgcolor: "background.paper" }}
              disabled={areaOptions.length === 0}
              helperText={areaOptions.length === 0 ? "امور فعالی برای این شرکت تعریف نشده است" : undefined}
            >
              {areaOptions.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.title}
                </MenuItem>
              ))}
            </TextField>
          </>
        ) : null}
        <Box>
          <Button
            size="small"
            variant="contained"
            disabled={saving || (areaSelectionRequired && !areaId)}
            onClick={refer}
          >
            ارجاع به کارشناس توزیع
          </Button>
        </Box>
      </Stack>
    </Alert>
  );
};

export default ReferToDistributionPanel;
